/**
 * ParkedCallsPanel (Phase 4b — park / transfer)
 *
 * A shared, team-wide list of calls parked on hold, each pick-up-able by any
 * agent. Mirrors MyTasksPanel: renders NOTHING when there are no parked calls.
 * Desktop (lg+): fixed bottom-LEFT (z-40) so it never collides with the
 * SoftphoneBar (bottom-right, z-50). Mobile (<lg): both docks share the bottom
 * edge, so this lifts above the softphone area (full-width within margins) to
 * stay reachable while on a call. Reads SoftphoneContext (parkedCalls + pickUp).
 * Pick up is disabled while the agent is already on a call (single-call model).
 */
import { PhoneCall, Pause } from 'lucide-react';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { Z } from './ui';

function waitedLabel(parkedAt: string | null): string {
  if (!parkedAt) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(parkedAt).getTime()) / 1000));
  const m = Math.floor(secs / 60);
  return m > 0 ? `${m}m` : `${secs}s`;
}

export default function ParkedCallsPanel() {
  const { parkedCalls, pickUp, callStatus } = useSoftphone();
  if (!parkedCalls || parkedCalls.length === 0) return null;
  const busy = callStatus !== 'idle';

  return (
    <div className={`fixed bottom-[calc(22.5rem+env(safe-area-inset-bottom))] left-4 right-4 ${Z.overlay} w-auto rounded-xl border border-amber-300 bg-white shadow-lg dark:border-amber-800 dark:bg-gray-800 lg:bottom-4 lg:right-auto lg:w-64`}>
      <div className="flex items-center gap-1.5 border-b border-gray-100 px-3 py-2 dark:border-gray-700">
        <Pause className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
          Parked calls ({parkedCalls.length})
        </span>
      </div>
      <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto dark:divide-gray-700">
        {parkedCalls.map((p) => (
          <li key={p.customerLegSid} className="flex items-center gap-2 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {p.name || p.from || 'Unknown caller'}
              </p>
              <p className="text-[11px] text-gray-400">
                On hold{p.parkedAt ? ` · ${waitedLabel(p.parkedAt)}` : ''}
              </p>
            </div>
            <button
              onClick={() => pickUp(p.confName, p.customerLegSid)}
              disabled={busy}
              title={busy ? 'Finish your current call first' : 'Pick up'}
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
            >
              <PhoneCall className="h-3.5 w-3.5" /> Pick up
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
